import React, { useMemo, useState } from "react";
import styled from "styled-components";
import SEO from "../components/SEO";
import { firestore } from "../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

/* ---------- Layout ---------- */

const Page = styled.main`
  min-height: 70vh;
  padding: 40px 18px;
  background: #f7fafc;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Hero = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 22px;
  margin-bottom: 18px;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(26px, 3vw, 44px);
  color: #111827;
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: #4b5563;
  line-height: 1.65;
  max-width: 820px;
`;

/* ---------- Tabs ---------- */

const Tabs = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

const Tab = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "#0f766e" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#111827")};
  font-weight: 900;
  border-radius: 999px;
  padding: 10px 16px;
  cursor: pointer;
  transition: transform 160ms ease, filter 160ms ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.02);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

/* ---------- Card ---------- */

const Card = styled.section`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 18px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 10px;
  font-size: 18px;
  font-weight: 950;
  color: #111827;
`;

const Muted = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 13px;
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  gap: 12px;

  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 900;
  color: #111827;
  font-size: 13px;
`;

const Input = styled.input`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 800;
  color: #111827;

  &:focus {
    outline: none;
    border-color: rgba(15, 118, 110, 0.45);
    box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.12);
  }
`;

const Select = styled.select`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 900;
  color: #111827;
  background: #fff;

  &:focus {
    outline: none;
    border-color: rgba(15, 118, 110, 0.45);
    box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.12);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 800;
  color: #111827;
  min-height: 150px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: rgba(15, 118, 110, 0.45);
    box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.12);
  }
`;

const Actions = styled.div`
  margin-top: 14px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;
  transition: transform 140ms ease, background 140ms ease;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const PrimaryBtn = styled(Btn)`
  border: 1px solid rgba(15, 118, 110, 0.25);
  background: rgba(15, 118, 110, 0.1);
  color: #0f766e;

  &:hover {
    filter: brightness(1.04);
  }
`;

const Alert = styled.div`
  border-radius: 16px;
  padding: 12px 14px;
  font-weight: 900;
  font-size: 13px;
  margin-bottom: 12px;

  border: 1px solid ${({ $type }) => ($type === "ok" ? "rgba(15,118,110,0.25)" : "rgba(233,78,27,0.30)")};
  background: ${({ $type }) => ($type === "ok" ? "rgba(15,118,110,0.08)" : "rgba(233,78,27,0.08)")};
  color: ${({ $type }) => ($type === "ok" ? "#0f766e" : "#e94e1b")};
`;

const InfoBox = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 14px;
  background: #fafafa;
`;

const InfoLine = styled.div`
  font-weight: 850;
  color: #111827;
  margin: 6px 0;

  span {
    color: #4b5563;
    font-weight: 800;
  }
`;

/* ---------- Helpers ---------- */

const isEmail = (s) => /^\S+@\S+\.\S+$/.test(String(s || "").trim());

/* ---------- Component ---------- */

export default function ContactPage() {
  const topics = useMemo(
    () => [
      { value: "general", label: "Allmän fråga" },
      { value: "booking", label: "Boka grupp / event" },
      { value: "membership", label: "Medlemskap" },
      { value: "sponsor", label: "Sponsring" },
      { value: "error", label: "Fel på sidan" },
    ],
    []
  );

  const [activeTab, setActiveTab] = useState("form");

  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: topics[0].value,
    message: "",
  });

  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // {type:"ok"|"err", text:""}

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return "Skriv ditt namn.";
    if (!form.email.trim()) return "Skriv din e-post.";
    if (!isEmail(form.email)) return "Skriv en giltig e-postadress.";
    if (!form.message.trim() || form.message.trim().length < 10)
      return "Skriv ett meddelande (minst 10 tecken).";
    return null;
  };

  const reset = () => {
    setStatus(null);
    setForm({ name: "", email: "", topic: topics[0].value, message: "" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    const err = validate();
    if (err) return setStatus({ type: "err", text: err });

    setBusy(true);
    try {
      await addDoc(collection(firestore, "contact_messages"), {
        name: form.name.trim(),
        email: form.email.trim(),
        topic: form.topic,
        message: form.message.trim(),
        createdAt: serverTimestamp(),
        status: "new",
      });

      setStatus({ type: "ok", text: "Tack! Ditt meddelande är skickat." });
      reset();
    } catch {
      setStatus({ type: "err", text: "Kunde inte skicka just nu. Testa igen om en stund." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO
        title="Kontakt"
        description="Kontakta Hofors BGK."
        keywords="kontakt, hofors bgk"
      />

      <Page>
        <Container>
          <Hero>
            <div>
              <Title>Kontakt</Title>
              <Subtitle>
                Välj ämne och skriv ditt meddelande så återkommer vi via e-post.
              </Subtitle>
            </div>
          </Hero>

          <Tabs>
            <Tab type="button" $active={activeTab === "form"} onClick={() => setActiveTab("form")}>
              Kontaktformulär
            </Tab>
            <Tab type="button" $active={activeTab === "info"} onClick={() => setActiveTab("info")}>
              Kontaktinfo
            </Tab>
          </Tabs>

          <Card>
            {activeTab === "form" && (
              <>
                <SectionTitle>Skicka meddelande</SectionTitle>
                <Muted>Fyll i fälten nedan. Meddelandet sparas och kan läsas i admin.</Muted>

                <div style={{ height: 12 }} />
                {status ? <Alert $type={status.type}>{status.text}</Alert> : null}

                <form onSubmit={onSubmit}>
                  <Grid>
                    <Field>
                      <Label>Namn</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="Ditt namn"
                        autoComplete="name"
                      />
                    </Field>

                    <Field>
                      <Label>E-post</Label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder="namn@exempel.se"
                        autoComplete="email"
                      />
                    </Field>
                  </Grid>

                  <div style={{ height: 12 }} />

                  <Field>
                    <Label>Ämne</Label>
                    <Select value={form.topic} onChange={(e) => set("topic", e.target.value)}>
                      {topics.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <div style={{ height: 12 }} />

                  <Field>
                    <Label>Meddelande</Label>
                    <Textarea
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      placeholder="Skriv ditt meddelande här…"
                    />
                  </Field>

                  <Actions>
                    <PrimaryBtn type="submit" disabled={busy}>
                      {busy ? "Skickar…" : "Skicka"}
                    </PrimaryBtn>
                    <Btn type="button" onClick={reset} disabled={busy}>
                      Rensa
                    </Btn>
                  </Actions>
                </form>
              </>
            )}

            {activeTab === "info" && (
              <>
                <SectionTitle>Kontaktinfo</SectionTitle>
                <Muted>Här hittar du våra kontaktuppgifter och bra att veta.</Muted>

                <div style={{ height: 12 }} />

                <InfoBox>
                  <InfoLine>E-post: <span>info@hoforsbgk.se</span></InfoLine>
                  <InfoLine>Telefon: <span>(lägg in nummer)</span></InfoLine>
                  <InfoLine>Adress: <span>(lägg in adress)</span></InfoLine>
                  <InfoLine>Svarstid: <span>1–2 dagar</span></InfoLine>
                </InfoBox>
              </>
            )}
          </Card>
        </Container>
      </Page>
    </>
  );
}

